import { defineStore } from 'pinia'
import * as api from '@/services/user-management.js'
import WebTrekk from '@/plugins/webtrekk/WebTrekk'

export const defaultState = {
    user: {
        ajaxCorrelationId: null,
        failureDialog: {
            show: false,
            error: null,
            headline: null,
            msg: null,
            dataQa: '',
            dialogBtnText: '',
            dialogBtnEmit: '',
            dialogBtnEmitLinkTarget: ''
        },
        submitting: false,
        submitted: false,
        fetching: false,
        fetched: false,
        authenticated: false,
        isBotRegistration: false,
        user: {
            salutation: null,
            title: null,
            genderCode: null,
            dateOfBirth: null,
            department: null,
            position: null,
            telephoneNumber: null,
            preferredLanguage: null,
            givenName: null,
            surName: null,
            companyName: null,
            unverifiedCrefonummer: null,
            email: null,
            password: null,
            consentMarketing: 'NOT_EXPRESSED',
            consentMarketingLastUpdatedAt: null,
            consentRecherche: 'NOT_EXPRESSED',
            consentRechercheLastUpdatedAt: null,
            company: null,
            crefonummer: null,
            id: null,
            postRegistrationKey: null,
            postRegistrationToken: null
        },
        security: {
            twoFAActive: false,
            credentialId: null
        },
        company: {
            crefonummer: '1',
            representatives: []
        },
        error: null,
        optin: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        resendOptin: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        patchPerson: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        patchPersonCompany: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        setNewEmail: {
            submitting: false,
            submitted: false,
            success: false,
            error: null,
            newEmail: ''
        },
        resendSetNewEmail: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        confirmUpdatedEmail: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        requestAccountDeletion: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        requestAccountDeletionResendEmail: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        confirmAccountDeletion: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        setNewPassword: {
            submitting: false,
            submitted: false,
            success: false,
            error: null,
            newEmail: ''
        },
        passwordForgotten: {
            email: null,
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        passwordReset: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        registerMembership: {
            submitting: false,
            submitted: false,
            success: false,
            error: null
        },
        redirectAfterLoginRoutes: {},
        defaultWebTarget: {
            key: 'MC',
            url: 'https://meine.creditreform.de'
        },
        webTarget: {
            key: null
        }
    }
}

const getErrorMessage = err => {
    return !err?.response?.data ? 'Unknown error' : err.response.data
}

const trackActionWithWebTrekk = trackName => {
    WebTrekk.action({
        name: trackName
    })
    WebTrekk.trackAction()
}

export const useUserStore = defineStore('user', {
    state: () => ({ ...defaultState }),
    actions: {
        authenticated(data) {
            this.user.authenticated = data
        },
        tokenUpdated(data) {
            this.user.authenticated = data
        },
        logout() {
            this.user = defaultState.user
        },
        resetProfileError() {
            this.user.submitting = false
            this.user.failureDialog.show = false
            this.user.failureDialog.error = null
            this.user.failureDialog.headline = null
            this.user.failureDialog.msg = null
            this.user.failureDialog.dataQa = ''
            this.user.failureDialog.dialogBtnText = ''
            this.user.failureDialog.dialogBtnEmit = ''
            this.user.failureDialog.dialogBtnEmitLinkTarget = ''
            this.user.ajaxCorrelationId = null
        },
        toggleDialog(data) {
            this.user.failureDialog = data
        },
        setWebTarget(webTargetConfig) {
            this.user.webTarget = webTargetConfig
        },
        getWebTarget(key) {
            api.getWebTarget(key)
                .then(response => {
                    this.user.webTarget = response.data
                })
                .catch(err => {
                    console.log('error getting web target for key ' + key, err)
                })
        },
        register(user) {
            // this is our honeypot field. if this is set, user is a bot.
            if (user.city && user.city.length) {
                this.user.submitting = false
                this.user.submitted = true
                this.user.error = null
                this.user.user = user
                this.user.isBotRegistration = true
            }

            this.user.submitting = true
            this.user.submitted = false
            this.user.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_register_form-submitted'
            )

            let registrationJson = {
                salutation:
                    user.salutation === 'UNKNOWN' ? null : user.salutation,
                givenName: user.givenName ?? user.firstName,
                surname: user.surname ?? user.surName,
                email: user.email,
                password: user.password,
                consentMarketing: user.consentMarketing ? 'GIVEN' : 'REFUSED',
                consentRecherche: user.consentRecherche ? 'GIVEN' : 'REFUSED',
                postRegistrationTargetKey: user.postRegistrationKey,
                postRegistrationToken: user.postRegistrationToken
            }

            if (user.crefonummer !== null) {
                registrationJson.crefonummer = user.crefonummer
            }

            if (user.ctoZugangsdaten) {
                registrationJson.ctoZugangsdaten = user.ctoZugangsdaten
            }

            if (user.preferredLanguage) {
                registrationJson.preferredLanguage = user.preferredLanguage
            }

            api.registerPerson(registrationJson)
                .then(() => {
                    this.user.submitting = false
                    this.user.submitted = true
                    this.user.error = null
                    this.user.user = user
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_register_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.fetching = false
                    this.user.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk('benutzerverwaltung_register_error')
                })
        },
        optin(data) {
            if (data.optinId === '123') {
                this.user.submitting = false
                this.user.fetching = false
                this.user.optin.submitted = true
                this.user.optin.success = true
            } else {
                api.confirmMailAddress(data.optinId)
                    .then(() => {
                        this.user.submitting = false
                        this.user.fetching = false
                        this.user.optin.submitted = true
                        this.user.optin.success = true
                        /* istanbul ignore next */
                        api.getWebTarget(data.prk)
                            .then(response => {
                                WebTrekk.action({
                                    name: 'benutzerverwaltung_register_optin_success',
                                    goal: { 16: data.prk },
                                    parameter: {
                                        18: response.data.sourceSystem
                                            .toLowerCase()
                                            .replace('_', ''),
                                        19: data.prt
                                    }
                                })
                                WebTrekk.trackAction()
                            })
                            .catch(err => {
                                console.log(
                                    'error getting source system for prk ',
                                    err
                                )
                            })
                    })
                    .catch(err => {
                        this.user.submitting = false
                        this.user.fetching = false
                        this.user.optin.submitted = true
                        this.user.optin.error = getErrorMessage(err)
                        this.user.optin.success = false
                        this.user.ajaxCorrelationId =
                            err?.response?.headers?.correlationid
                        trackActionWithWebTrekk(
                            'benutzerverwaltung_register_optin_error'
                        )
                    })
            }
        },

        resendOptin(email) {
            this.user.resendOptin.submitted = false
            this.user.resendOptin.success = false
            this.user.resendOptin.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_resend-optin_form-submitted'
            )

            api.resendOptInMail(email)
                .then(() => {
                    this.user.resendOptin.submitted = true
                    this.user.resendOptin.success = true
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_resend-optin_success'
                    )
                })
                .catch(err => {
                    this.user.resendOptin.submitted = true
                    this.user.resendOptin.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_resend-optin_error'
                    )
                })
        },

        requestAccountDeletion() {
            this.user.requestAccountDeletion.submitting = true
            this.user.requestAccountDeletion.submitted = false
            this.user.requestAccountDeletion.success = false
            this.user.requestAccountDeletion.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_request-account-deletion_submitted'
            )

            api.requestDeletion()
                .then(() => {
                    this.user.requestAccountDeletion.submitting = false
                    this.user.requestAccountDeletion.submitted = true
                    this.user.requestAccountDeletion.success = true
                    this.user.requestAccountDeletion.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_request-account-deletion_success'
                    )
                })
                .catch(err => {
                    this.user.requestAccountDeletion.submitting = false
                    this.user.requestAccountDeletion.submitted = true
                    this.user.requestAccountDeletion.success = false
                    this.user.requestAccountDeletion.error =
                        getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_request-account-deletion_error'
                    )
                })
        },

        requestAccountDeletionResendEmail() {
            this.user.requestAccountDeletionResendEmail.submitting = true
            this.user.requestAccountDeletionResendEmail.submitted = false
            this.user.requestAccountDeletionResendEmail.success = false
            this.user.requestAccountDeletionResendEmail.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_request-account-deletion-resend-email_submitted'
            )
            api.requestDeletion()
                .then(() => {
                    this.user.requestAccountDeletionResendEmail.submitting = false
                    this.user.requestAccountDeletionResendEmail.submitted = true
                    this.user.requestAccountDeletionResendEmail.success = true
                    this.user.requestAccountDeletionResendEmail.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_request-account-deletion-resend-email_success'
                    )
                })
                .catch(err => {
                    this.user.requestAccountDeletionResendEmail.submitting = false
                    this.user.requestAccountDeletionResendEmail.submitted = true
                    this.user.requestAccountDeletionResendEmail.success = false
                    this.user.requestAccountDeletionResendEmail.error =
                        getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_request-account-deletion-resend-email_error'
                    )
                })
        },

        confirmAccountDeletion(optOutToken) {
            if (optOutToken === '##123##') {
                this.user.confirmAccountDeletion.submitting = false
                this.user.confirmAccountDeletion.submitted = true
                this.user.confirmAccountDeletion.success = true
                this.user.confirmAccountDeletion.error = null
            } else {
                this.user.confirmAccountDeletion.submitting = true
                this.user.confirmAccountDeletion.submitted = false
                this.user.confirmAccountDeletion.success = false
                this.user.confirmAccountDeletion.error = null
                trackActionWithWebTrekk(
                    'benutzerverwaltung_confirm-account-deletion-submitted'
                )
                api.confirmDeletion(optOutToken)
                    .then(() => {
                        this.user.confirmAccountDeletion.submitting = false
                        this.user.confirmAccountDeletion.submitted = true
                        this.user.confirmAccountDeletion.success = true
                        this.user.confirmAccountDeletion.error = null
                        trackActionWithWebTrekk(
                            'benutzerverwaltung_confirm-account-deletion_success'
                        )
                    })
                    .catch(err => {
                        this.user.confirmAccountDeletion.submitting = false
                        this.user.confirmAccountDeletion.submitted = true
                        this.user.confirmAccountDeletion.success = false
                        this.user.confirmAccountDeletion.error =
                            getErrorMessage(err)
                        this.user.ajaxCorrelationId =
                            err?.response?.headers?.correlationid
                        trackActionWithWebTrekk(
                            'benutzerverwaltung_confirm-account-deletion_error'
                        )
                    })
            }
        },

        registerMembership(payload) {
            this.user.submitting = true
            this.user.registerMembership.submitting = true
            this.user.registerMembership.submitted = false
            this.user.registerMembership.success = false
            this.user.registerMembership.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_register-membership_form-submitted'
            )

            api.registerMembership(payload)
                .then(() => {
                    this.user.submitting = false
                    this.user.registerMembership.submitting = false
                    this.user.registerMembership.submitted = true
                    this.user.registerMembership.success = true
                    this.user.registerMembership.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_register-membership_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.registerMembership.submitting = false
                    this.user.registerMembership.submitted = true
                    this.user.registerMembership.success = false
                    this.user.registerMembership.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_register-membership_error'
                    )
                })
        },

        getPersonMe() {
            this.user.submitting = true

            api.getPersonMe()
                .then(response => {
                    if (!response.data.unverifiedCrefonummer) {
                        this.user.error = null
                        this.user.submitting = false
                        this.user.user = response.data
                    } else {
                        /* istanbul ignore next */
                        api.searchSmartSignup(
                            response.data.unverifiedCrefonummer
                        )
                            .then(responseCompany => {
                                if (
                                    responseCompany.data
                                        .companyNameSuggestions[0]
                                        .crefonummer &&
                                    responseCompany.data
                                        .companyNameSuggestions[0].crefonummer
                                        .length &&
                                    responseCompany.data
                                        .companyNameSuggestions[0]
                                        .crefonummer ===
                                        response.data.unverifiedCrefonummer
                                ) {
                                    response.data.companyName =
                                        responseCompany.data.companyNameSuggestions[0].name
                                }
                            })
                            .catch(() => {
                                response.data.unverifiedCrefonummer = null
                            })
                            .finally(() => {
                                this.user.error = null
                                this.user.submitting = false
                                this.user.user = response.data
                            })
                    }
                })
                .catch(err => {
                    this.user.error = getErrorMessage(err)
                    this.user.submitting = false
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                })
        },

        patchPerson(data) {
            this.user.submitting = true
            this.user.patchPerson.submitting = true
            this.user.patchPerson.submitted = false
            this.user.patchPerson.success = false
            this.user.patchPerson.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_patch-person_form-submitted'
            )

            api.patchPersonMe(data)
                .then(() => {
                    this.user.submitting = false
                    this.user.patchPerson.submitting = false
                    this.user.patchPerson.submitted = true
                    this.user.patchPerson.success = true
                    this.user.patchPerson.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_patch-person_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.patchPerson.submitting = false
                    this.user.patchPerson.submitted = true
                    this.user.patchPerson.success = false
                    this.user.patchPerson.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_patch-person_error'
                    )
                })
        },

        patchPersonCompany({ data, crefoNummer, companyName }) {
            this.user.submitting = true
            this.user.patchPersonCompany.submitting = true
            this.user.patchPersonCompany.submitted = false
            this.user.patchPersonCompany.success = false
            this.user.patchPersonCompany.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_patch-person-company_form-submitted'
            )

            api.patchPersonMe(data)
                .then(() => {
                    this.user.submitting = false
                    this.user.patchPersonCompany.submitting = false
                    this.user.patchPersonCompany.submitted = true
                    this.user.patchPersonCompany.success = true
                    this.user.patchPersonCompany.error = null
                    this.user.user.companyName = companyName
                    this.user.user.unverifiedCrefoNumber = crefoNummer
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_patch-person-company_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.patchPersonCompany.submitting = false
                    this.user.patchPersonCompany.submitted = true
                    this.user.patchPersonCompany.success = false
                    this.user.patchPersonCompany.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_patch-person-company_error'
                    )
                })
        },

        getPersonMeMFACredentials() {
            this.user.submitting = true

            api.getPersonMFACredentials()
                .then(response => {
                    this.user.error = null
                    this.user.submitting = false
                    this.user.security.twoFAActive = response?.data?.length > 0
                    this.user.security.credentialId = response?.data?.find(credential => credential.type === "AUTHENTICATOR_APP")?.credentialId
                })
                .catch(err => {
                    this.user.error = err.msg
                    this.user.submitting = false
                    this.user.ajaxCorrelationId =
                        err.response?.headers?.correlationid
                    this.user.failureDialog.show = true
                    this.user.failureDialog.msg = true
                    this.user.failureDialog.dataQa = 'get-2fa-status-error'
                })
        },

        setPersonMeNewEmail(data) {
            this.user.submitting = true
            this.user.setNewEmail.submitting = true
            this.user.setNewEmail.submitted = false
            this.user.setNewEmail.success = false
            this.user.setNewEmail.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_set-new-email_form-submitted'
            )

            api.setPersonMeNewEmail(data)
                .then(() => {
                    this.user.submitting = false
                    this.user.setNewEmail.submitting = false
                    this.user.setNewEmail.submitted = true
                    this.user.setNewEmail.success = true
                    this.user.setNewEmail.error = null
                    this.user.setNewEmail.newEmail = data.newEmail
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_set-new-email_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.setNewEmail.submitting = false
                    this.user.setNewEmail.submitted = true
                    this.user.setNewEmail.success = false
                    this.user.setNewEmail.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_set-new-email_error'
                    )
                })
        },

        setNewEmailConfirm(optinId) {
            if (optinId === '123') {
                this.user.submitting = false
                this.user.fetching = false
                this.user.confirmUpdatedEmail.submitted = true
                this.user.confirmUpdatedEmail.success = true
            } else {
                api.confirmMailAddress(optinId)
                    .then(() => {
                        this.user.submitting = false
                        this.user.fetching = false
                        this.user.confirmUpdatedEmail.submitted = true
                        this.user.confirmUpdatedEmail.success = true
                        trackActionWithWebTrekk(
                            'benutzerverwaltung_confirm-new-email_success'
                        )
                    })
                    .catch(err => {
                        this.user.submitting = false
                        this.user.fetching = false
                        this.user.confirmUpdatedEmail.submitted = true
                        this.user.confirmUpdatedEmail.error =
                            getErrorMessage(err)
                        this.user.confirmUpdatedEmail.success = false
                        this.user.ajaxCorrelationId =
                            err?.response?.headers?.correlationid
                        trackActionWithWebTrekk(
                            'benutzerverwaltung_confirm-new-email_error'
                        )
                    })
            }
        },
        resendSetNewEmailOptin() {
            this.user.submitting = true
            this.user.resendSetNewEmail.submitting = true
            this.user.resendSetNewEmail.submitted = false
            this.user.resendSetNewEmail.success = false
            this.user.resendSetNewEmail.error = null

            trackActionWithWebTrekk(
                'benutzerverwaltung_resend-set-new-email-optin_form-submitted'
            )

            api.resendSetPersonMeNewEmail()
                .then(() => {
                    this.user.submitting = false
                    this.user.resendSetNewEmail.submitting = false
                    this.user.resendSetNewEmail.submitted = true
                    this.user.resendSetNewEmail.success = true
                    this.user.resendSetNewEmail.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_resend-set-new-email-optin_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.resendSetNewEmail.submitting = false
                    this.user.resendSetNewEmail.submitted = true
                    this.user.resendSetNewEmail.success = false
                    this.user.resendSetNewEmail.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_resend-set-new-email-optin_error'
                    )
                })
        },

        setPersonMeNewPassword(data) {
            this.user.submitting = true
            this.user.setNewPassword.submitting = true
            this.user.setNewPassword.submitted = false
            this.user.setNewPassword.success = false
            this.user.setNewPassword.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_set-new-password_form-submitted'
            )

            api.setPersonMeNewPassword(data)
                .then(() => {
                    this.user.submitting = false
                    this.user.setNewPassword.submitting = false
                    this.user.setNewPassword.submitted = true
                    this.user.setNewPassword.success = true
                    this.user.setNewPassword.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_set-new-password_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.setNewPassword.submitting = false
                    this.user.setNewPassword.submitted = true
                    this.user.setNewPassword.success = false
                    this.user.setNewPassword.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_set-new-password_error'
                    )
                })
        },

        requestPasswordResetMail(data) {
            this.user.submitting = true
            this.user.passwordForgotten.submitting = true
            this.user.passwordForgotten.submitted = false
            this.user.passwordForgotten.success = false
            this.user.passwordForgotten.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_password-forgotten_form-submitted'
            )

            api.requestPasswordResetMail({
                email: data.user?.passwordForgotten?.email
            })
                .then(() => {
                    this.user.submitting = false
                    this.user.passwordForgotten.submitting = false
                    this.user.passwordForgotten.submitted = true
                    this.user.passwordForgotten.success = true
                    this.user.passwordForgotten.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_password-forgotten_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.passwordForgotten.submitting = false
                    this.user.passwordForgotten.submitted = true
                    this.user.passwordForgotten.success = false
                    this.user.passwordForgotten.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_password-forgotten_error'
                    )
                })
        },

        resetPassword(data) {
            this.user.submitting = true
            this.user.passwordReset.submitting = true
            this.user.passwordReset.submitted = false
            this.user.passwordReset.success = false
            this.user.passwordReset.error = null
            trackActionWithWebTrekk(
                'benutzerverwaltung_reset-password_form-submitted'
            )

            api.resetPassword(data)
                .then(() => {
                    this.user.submitting = false
                    this.user.passwordReset.submitting = false
                    this.user.passwordReset.submitted = true
                    this.user.passwordReset.success = true
                    this.user.passwordReset.error = null
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_reset-password_success'
                    )
                })
                .catch(err => {
                    this.user.submitting = false
                    this.user.passwordReset.submitting = false
                    this.user.passwordReset.submitted = true
                    this.user.passwordReset.success = false
                    this.user.passwordReset.error = getErrorMessage(err)
                    this.user.ajaxCorrelationId =
                        err?.response?.headers?.correlationid
                    trackActionWithWebTrekk(
                        'benutzerverwaltung_reset-password_error'
                    )
                })
        }
    },
    getters: {
        correlationId(state) {
            return state.user.ajaxCorrelationId
        },

        isMitgliedsUser(state) {
            if (
                state.user.authenticated?.tokenParsed !== null &&
                typeof state.user.authenticated?.tokenParsed != 'undefined'
            ) {
                if (
                    state.user.authenticated?.tokenParsed?.cr_mapping &&
                    state.user.authenticated?.tokenParsed?.cr_mapping.length
                ) {
                    return true
                }
            }
            return false
        },

        hasPerson(state) {
            return !!state.user.authenticated?.tokenParsed?.cr_id
        },

        hasAnyVertretungsberechtigung(state) {
            const identifierVertretungsberechtigung =
                '.VERTRETUNGSBERECHTIGUNG;'
            const roles = state.user.authenticated.tokenParsed?.cr_userrole

            if (!roles) {
                return false
            }

            return !!roles.filter(value =>
                value.includes(identifierVertretungsberechtigung)
            ).length
        },

        is2FAActive(state) {
            return state.user.security.twoFAActive
        },

        getCredentialId(state) {
            return state.user.security.credentialId
        },

        showCompanyData() {
            if (!this.hasPerson) {
                console.log('[showCompanyData] person not loaded yet')
                return false
            }

            if (this.isMitgliedsUser) {
                console.log('[showCompanyData] person is mitglied')
                return false
            }

            if (this.hasAnyVertretungsberechtigung) {
                console.log(
                    '[showCompanyData] person has (any) vertretungsberechtigung'
                )
                return false
            }

            return true
        },

        getLogoLink(state) {
            if (
                state.user.webTarget?.key !== null &&
                state.user.webTarget?.key !== undefined
            ) {
                return state.user.webTarget?.url
            } else {
                return state.user.defaultWebTarget.url
            }
        },

        keycloak(state) {
            return state.user.authenticated
        },

        getUser(state) {
            return state.user
        },

        registrationFailedDueToCtoCredentials(state) {
            if (
                state.user.error !== null &&
                state.user.error instanceof Array &&
                state.user.error.length &&
                state.user.error[0].errorCode &&
                state.user.error[0].errorCode.length
            ) {
                return [
                    'MITGLIEDSNUMMER_UNKNOWN',
                    'MITGLIEDSNUMMER_NOT_YET_VALID',
                    'MITGLIEDSNUMMER_NO_LONGER_VALID',
                    'MITGLIEDSKENNUNG_NOT_ENABLED_FOR_PLATFORM',
                    'MITGLIEDSKENNUNG_LOCKED',
                    'CTO_ZUGANGSDATEN_INVALID',
                    'MITGLIEDSKENNUNG_ALREADY_REGISTERED'
                ].includes(state.user.error[0].errorCode)
            }
            return false
        },

        internalUser(state) {
            return state.user?.user
        },

        getFailureDialog(state) {
            return state.user.failureDialog
        },

        getWebTargetData(state) {
            return state.user.webTarget
        }
    }
})
